import React, { useEffect, useState } from 'react';
import { consultantsAPI } from '@/api/api';
import { Plus, Search, DollarSign, Users, Lock, Unlock, Edit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useNavigate } from 'react-router-dom';

const ConsultantList = () => {
  const navigate = useNavigate();
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchConsultants();
  }, []);

  const fetchConsultants = async () => {
    try {
      const response = await consultantsAPI.getConsultants();
      setConsultants(response.data);
    } catch (error) {
      console.error('Error fetching consultants:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBlockConsultant = async (consultantId, consultantName) => {
    if (window.confirm(`Are you sure you want to block ${consultantName}?`)) {
      try {
        await consultantsAPI.blockConsultant(consultantId);
        alert('Consultant blocked successfully!');
        fetchConsultants();
      } catch (error) {
        console.error('Error blocking consultant:', error);
        alert('Failed to block consultant. Please try again.');
      }
    }
  };

  const handleUnblockConsultant = async (consultantId, consultantName) => {
    if (window.confirm(`Are you sure you want to unblock ${consultantName}?`)) {
      try {
        await consultantsAPI.unblockConsultant(consultantId);
        alert('Consultant unblocked successfully!');
        fetchConsultants();
      } catch (error) {
        console.error('Error unblocking consultant:', error);
        alert('Failed to unblock consultant. Please try again.');
      }
    }
  };

  const handleEditConsultant = (consultantId) => {
    navigate(`/consultants/edit/${consultantId}`);
  };

  const handleDeleteConsultant = async (consultantId, consultantName) => {
    if (window.confirm(`Are you sure you want to delete ${consultantName}? This action cannot be undone.`)) {
      try {
        await consultantsAPI.deleteConsultant(consultantId);
        alert('Consultant deleted successfully!');
        fetchConsultants();
      } catch (error) {
        console.error('Error deleting consultant:', error);
        alert(error?.response?.data?.detail || 'Failed to delete consultant. Please try again.');
      }
    }
  };

  const filteredConsultants = consultants.filter(consultant =>
    consultant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    consultant.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64" data-testid="loading">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="consultant-list-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Consultant Management</h1>
          <p className="text-gray-600 mt-1">Manage admission consultants</p>
        </div>
        <Button 
          className="bg-blue-600 hover:bg-blue-700" 
          data-testid="add-consultant-button"
          onClick={() => navigate('/consultants/add')}
        >
          <Plus size={20} className="mr-2" /> Add Consultant
        </Button>
      </div>

      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-3 text-gray-400" size={20} />
          <Input
            placeholder="Search consultants by name or email..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-consultants-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredConsultants.length === 0 ? (
          <div className="col-span-full bg-white p-12 rounded-xl shadow-sm border border-gray-200 text-center">
            <Users size={64} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No consultants found.</p>
          </div>
        ) : (
          filteredConsultants.map((consultant) => (
            <div key={consultant.id} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow" data-testid={`consultant-card-${consultant.id}`}>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users size={32} className="text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800">{consultant.name}</h3>
                  <p className="text-sm text-gray-500">{consultant.email}</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Phone:</span>
                  <span className="text-sm font-medium text-gray-800">{consultant.phone}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Status:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    consultant.is_blocked ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                  }`}>
                    {consultant.is_blocked ? 'Blocked' : 'Active'}
                  </span>
                </div>
              </div>

              {/* ============================================================ */}
              {/* ⚠️ LOCKED CODE - DO NOT MODIFY - Course-wise Payout Display */}
              {/* This displays consultant payout settings configured per course */}
              {/* Last Updated: Dec 2024 - Tested & Verified */}
              {/* ============================================================ */}
              {consultant.course_payout_settings && consultant.course_payout_settings.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs font-semibold text-gray-600 mb-2">Course-wise Payouts:</p>
                  <div className="space-y-1">
                    {consultant.course_payout_settings.map((payout, idx) => (
                      <div key={idx} className="text-xs">
                        <p className="font-medium text-gray-700">{payout.course_name}</p>
                        <div className="flex gap-3 ml-2 mt-0.5">
                          <span className="text-gray-600">Regular: <span className="font-semibold text-green-600">₹{payout.regular_payout?.toLocaleString('en-IN') || 0}</span></span>
                          <span className="text-gray-600">Lateral: <span className="font-semibold text-green-600">₹{payout.lateral_payout?.toLocaleString('en-IN') || 0}</span></span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* ============================================================ */}
              {/* END OF LOCKED CODE */}
              {/* ============================================================ */}

              <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full" 
                  size="sm" 
                  data-testid={`view-ledger-${consultant.id}`}
                  onClick={() => navigate(`/consultants/ledger?consultant_id=${consultant.id}`)}
                >
                  <DollarSign size={16} className="mr-2" /> View Ledger
                </Button>
                
                <div className="grid grid-cols-2 gap-2">
                  <Button 
                    variant="outline" 
                    className="w-full border-blue-500 text-blue-600 hover:bg-blue-50" 
                    size="sm"
                    data-testid={`edit-consultant-${consultant.id}`}
                    onClick={() => handleEditConsultant(consultant.id)}
                  >
                    <Edit size={16} className="mr-1" /> Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full border-red-500 text-red-600 hover:bg-red-50" 
                    size="sm"
                    data-testid={`delete-consultant-${consultant.id}`}
                    onClick={() => handleDeleteConsultant(consultant.id, consultant.name)}
                  >
                    <Trash2 size={16} className="mr-1" /> Delete
                  </Button>
                </div>
                
                {consultant.is_blocked ? (
                  <Button 
                    variant="outline" 
                    className="w-full border-green-500 text-green-600 hover:bg-green-50" 
                    size="sm"
                    onClick={() => handleUnblockConsultant(consultant.id, consultant.name)}
                  >
                    <Unlock size={16} className="mr-2" /> Unblock
                  </Button>
                ) : (
                  <Button 
                    variant="outline" 
                    className="w-full border-orange-500 text-orange-600 hover:bg-orange-50" 
                    size="sm"
                    onClick={() => handleBlockConsultant(consultant.id, consultant.name)}
                  >
                    <Lock size={16} className="mr-2" /> Block
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
          <p className="text-blue-600 text-sm font-medium">Total Consultants</p>
          <p className="text-3xl font-bold text-blue-700 mt-2" data-testid="total-consultants-stat">{consultants.length}</p>
        </div>
        <div className="bg-green-50 p-6 rounded-xl border border-green-200">
          <p className="text-green-600 text-sm font-medium">Active Consultants</p>
          <p className="text-3xl font-bold text-green-700 mt-2">{consultants.filter(c => !c.is_blocked).length}</p>
        </div>
        <div className="bg-purple-50 p-6 rounded-xl border border-purple-200">
          <p className="text-purple-600 text-sm font-medium">Avg Commission</p>
          <p className="text-3xl font-bold text-purple-700 mt-2">
            ₹{consultants.length > 0 ? Math.round(consultants.reduce((sum, c) => sum + c.commission_rate, 0) / consultants.length) : 0}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConsultantList;
